import { useState } from 'react';
import FileUpload from '../components/core/FileUpload';
import TextArea from '../components/forms/TextArea';
import TextField from '../components/forms/TextField';
import { INPUT_STATES } from '../components/forms/forms.utils';
import { MODALS } from '../constants/modals.constants';
import formValidators from '../form-validators';
import { useForm } from '../hooks/useForm.hook';
import responseValidators from '../services/response.validators';
import Modal from './Modal';
import ModalTitle from './ModalTitle';

const AttachmentUploadModal = ({
  isOpen,
  onClose,
  onConfirm,
  inline,
  descriptionLabel = 'Beschrijving',
  descriptionDescription = ''
}) => {
  const { data, input, load, reset, isSubmitting, error, submitStart, submitOk, submitError } = useForm(
    MODALS.ATTACHMENT_UPLOAD.initialState
  );
  const [urlValidation, setUrlValidation] = useState('');

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    reset();
    load(MODALS.ATTACHMENT_UPLOAD.initialState);
    onClose && onClose();
  };

  const handleConfirm = () => {
    if (inline) {
      onConfirm(data);
      handleClose();
    } else {
      submitStart();
      onConfirm(data)
        .then((resp) => {
          responseValidators.uploadForDossier(resp);
          submitOk();
          handleClose();
        })
        .catch((err) => {
          submitError(err.message);
        });
    }
  };

  const resetUrlValidation = () => setUrlValidation('');

  const handleUrlInput = (value) => {
    if (!!value) {
      const validation = formValidators.attachmentUrl(value);
      setUrlValidation(validation);
    } else {
      resetUrlValidation();
    }
    input(value, 'url');
  };

  const handleAttachmentAdd = (files) => {
    resetUrlValidation();
    input(files[0], 'attachment');
  };

  const handleAttachmentDelete = () => {
    resetUrlValidation();
    input(null, 'attachment');
  };

  return (
    <Modal
      className="spo-o-attachment-upload-modal"
      title={
        <ModalTitle
          hasError={!!error}
          title="Bestand of URL toevoegen"
          subtitle="Je kan ervoor kiezen een URL naar een Sharepoint bestand te delen of om rechtstreeks een bestand te uploaden."
        />
      }
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      canConfirm={!!data.attachment || !!data.url}
      isSubmitting={isSubmitting}
      onConfirmLabel="Toevoegen"
      onConfirmIcon="ai-check-1"
    >
      <div className="row">
        <div className="col-xs-12 col-md-6 u-margin-bottom">
          <TextField
            label="Titel van bestand of URL"
            value={data.name}
            state={INPUT_STATES.DEFAULT}
            onChange={(value) => input(value, 'name')}
            disabled={isSubmitting}
          />
        </div>
        <div className="col-xs-12 col-md-6 u-margin-bottom">
          <TextField
            label="URL (https://digipolis.sharepoint.com/...)"
            value={data.attachment ? '' : data.url}
            state={INPUT_STATES.DEFAULT}
            onChange={handleUrlInput}
            disabled={isSubmitting || !!data.attachment}
            hasError={!!urlValidation}
            validationError={urlValidation}
          />
        </div>
      </div>
      <div className="row u-margin-bottom">
        <div className="col-xs-12">
          <TextArea
            label={descriptionLabel}
            description={descriptionDescription}
            value={data.description}
            state={INPUT_STATES.DEFAULT}
            onChange={(value) => input(value, 'description')}
            disabled={isSubmitting}
          />
        </div>
      </div>
      <div className="u-margin-bottom">
        <FileUpload
          required
          disabled={!!data.url}
          files={data.attachment && !data.url ? [data.attachment] : []}
          onChange={handleAttachmentAdd}
          onDelete={handleAttachmentDelete}
        />
      </div>
      {error && <small className="u-text-danger">{error}</small>}
    </Modal>
  );
};

export default AttachmentUploadModal;
